import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useTranslation } from 'next-i18next';

import { useAnalytics } from '@restworld/utils-common';
import { JobPositionCardProps } from '../legacy';
import { JobPositionsCardV2 } from '../legacy';
import { useAuth } from '@restworld/feature-authentication';

import { restaurantDetailsAtom, ShowcaseSectionTitle } from '.';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IVenueJobPositionsProps {}

const VenueJobPositions: React.FC<IVenueJobPositionsProps> = () => {
  const restaurantDetails = useAtomValue(restaurantDetailsAtom);
  const activeJobPositions = React.useMemo(() => restaurantDetails?.activeJobPositions, [restaurantDetails]);

  const auth = useAuth();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('restaurant-showcase-v2');

  const recordViewJobPositionEvent = React.useCallback(
    (jobProps: Partial<JobPositionCardProps>) => {
      trackEvent('view_job', {
        email: auth?.user?.email,
        role: auth?.user?.type,
        origin: 'restaurant_showcase_page',
        id: jobProps?.id,
        job_title: jobProps?.jobTitle,
        occupation: jobProps?.occupation,
        salary_min: jobProps?.salaryMin,
        salary_max: jobProps?.salaryMax,
        job_type: jobProps?.jobType,
        published_at: jobProps?.publishedAt,
        restaurant_name: jobProps?.restaurantName,
        city: jobProps?.city,
      });
    },
    [auth?.user?.email, auth?.user?.type, trackEvent]
  );

  const renderJobCards = React.useMemo(() => {
    if (!activeJobPositions?.length)
      return <h5 className="text-neutral-700 text-base font-normal">{t('no_open_positions')}</h5>;

    return (activeJobPositions || [])?.flatMap(
      (domain) =>
        (domain?.jobs || [])?.map((job) => (
          <JobPositionsCardV2
            key={job?.id}
            jobTitle={job?.jobTitle}
            businessName={job?.restaurantName}
            venueAddress={restaurantDetails?.mapLocation?.address}
            occupation={job?.occupation}
            openJobPositionsCount={job?.staffRequested}
            restaurantLogoUrl={job?.restaurantLogo || ''}
            restaurantHrefLink={job?.redirectUrl || '#!'}
            onClick={(e) => {
              e.stopPropagation();
              recordViewJobPositionEvent(job);
            }}
          />
        )) ?? []
    );
  }, [activeJobPositions, recordViewJobPositionEvent, restaurantDetails?.mapLocation?.address, t]);

  return (
    activeJobPositions?.length > 0 && (
      <div id="job-positions" className="self-stretch inline-flex flex-col justify-start items-start gap-4">
        <ShowcaseSectionTitle title={t('open_job_positions')} />
        <div className="self-stretch p-2 bg-purple-50 rounded-2xl flex flex-col justify-start items-start gap-4">
          {renderJobCards}
        </div>
      </div>
    )
  );
};

export default VenueJobPositions;
